/**
 * ClaimSync Waitlist Page
 * For external users to sign up for early access
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Clock, Shield, Zap, BarChart3 } from 'lucide-react';
import api from '../utils/api';

export default function WaitlistPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      // Use centralized API utility (no auth required for waitlist)
      await api.post('/api/waitlist', formData, { showErrorToast: false });
      setStatus('success');
    } catch (error: any) {
      setErrorMessage(error.message || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-8">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">You're on the list!</h1>
          <p className="text-blue-200 mb-8">
            Thanks for your interest in ClaimSync. We'll reach out when we're ready to bring you on board.
          </p>
          <Link 
            to="/home" 
            className="inline-flex items-center gap-2 text-[#2762EA] hover:text-blue-400 font-medium"
          >
            Back to Homepage <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A1628] flex-col justify-between p-12">
        {/* Logo */}
        <div>
          <Link to="/home">
            <img 
              src="/logo.svg" 
              alt="ClaimSync" 
              className="h-44 lg:h-56 w-auto"
            />
          </Link>
        </div>

        {/* Main Content */}
        <div className="max-w-xl">
          <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
            Get Early Access to ClaimSync
          </h1>
          <p className="text-lg text-blue-200 mb-10 leading-relaxed">
            Join our waitlist to be among the first to experience AI-powered business interruption claim calculations.
          </p>

          {/* Features */}
          <div className="grid grid-cols-2 gap-4">
            <FeatureCard icon={<Clock className="w-5 h-5" />} title="Save Time" description="Hours of work reduced to minutes" />
            <FeatureCard icon={<Shield className="w-5 h-5" />} title="Accuracy" description="AI-verified calculations" />
            <FeatureCard icon={<Zap className="w-5 h-5" />} title="Automated" description="Smart transaction classification" />
            <FeatureCard icon={<BarChart3 className="w-5 h-5" />} title="Reports" description="Professional PDF generation" />
          </div>
        </div>

        {/* Footer */}
        <p className="text-slate-500 text-sm">
          © 2025 ClaimSync. Professional business interruption claim calculations.
        </p>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/home">
              <img 
                src="/logo.svg" 
                alt="ClaimSync" 
                className="h-32 w-auto mx-auto mb-4"
              />
            </Link>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Join the Waitlist</h2>
            <p className="text-gray-600">Be the first to know when we launch</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1.5">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2762EA] focus:border-transparent"
                  placeholder="John"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2762EA] focus:border-transparent"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Work Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2762EA] focus:border-transparent"
                placeholder="john@company.com"
              />
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1.5">
                Company
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2762EA] focus:border-transparent"
                placeholder="Acme Inc."
              />
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1.5">
                Your Role
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2762EA] focus:border-transparent bg-white"
              >
                <option value="">Select your role...</option>
                <option value="accountant">Accountant / CPA</option>
                <option value="attorney">Attorney</option>
                <option value="insurance">Insurance Professional</option>
                <option value="consultant">Consultant</option>
                <option value="other">Other</option>
              </select>
            </div>

            {status === 'error' && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full bg-[#2762EA] hover:bg-[#1e50c7] text-white font-semibold py-3 px-6 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {status === 'loading' ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Joining...
                </>
              ) : (
                'Join Waitlist'
              )}
            </button>

            <p className="text-xs text-gray-500 text-center">
              By joining, you agree to our{' '}
              <Link to="/privacy" className="text-[#2762EA] hover:underline">Privacy Policy</Link>
              {' '}and{' '}
              <Link to="/terms" className="text-[#2762EA] hover:underline">Terms of Service</Link>.
            </p>
          </form>

          {/* Already have access */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm">
              Already have an account?{' '}
              <Link to="/sign-in" className="text-[#2762EA] hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
      <div className="text-[#2762EA] mb-2">{icon}</div>
      <h3 className="text-white font-semibold text-sm mb-1">{title}</h3>
      <p className="text-blue-200/70 text-xs">{description}</p>
    </div>
  );
}
